/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { observer } from 'mobx-react';
import { Paper, Button } from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { Theme } from '@material-ui/core/styles';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import {
  Box,
  Divider,
  Grid,
  List,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { useStore } from '../../hooks';
import CustomListItem from '../../components/customListItem';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      margin: '0 auto',
      borderRadius: theme.spacing(2),
      padding: theme.spacing(3, 0),
      boxShadow:
        'rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px',
    },
    textBold: {
      fontWeight: 700,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    propertyAddress: {
      padding: theme.spacing(0, 4, 1, 4),
    },
    propertyDivider: {
      margin: theme.spacing(1, 2),
    },
  })
);

interface Props {
  handleNextStep: () => void;
}

export const InformationStep = observer((props: Props) => {
  const { paper, textBold, loadingContainer, propertyAddress, propertyDivider } = useStyles();
  const { valuationPaymentStore } = useStore();
  const { loading, valuationInfo } = valuationPaymentStore;

  if (loading) {
    return (
      <Box className={loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  const renderProperties = () => {
    if (!valuationInfo?.properties?.length) {
      return (
        <CustomListItem
          label="Property Address(es)"
          value="N/A"
        />
      );
    }

    return valuationInfo.properties.map((property, index) => (
      <Box key={`${property.address}-${index}`}>

        <Box style={{ padding: '0 32px 4px 32px' }}>
          {property.address}
        </Box>

        <Box style={{ paddingLeft: 24 }}>

          {property.addOns?.map((addOn, addOnIndex) => (
            <CustomListItem
              key={`${addOn.type}-${addOnIndex}`}
              label={addOn.type}
              value={formatCurrency(Number(addOn.addOnCost) || 0)}
            />
          ))}

        </Box>

        {index < valuationInfo.properties.length - 1 && (
          <Divider style={{ margin: '8px 16px' }} />
        )}

      </Box>
    ));
  };

  const renderValuationDetails = () => (
    <Paper className={paper}>
      <Box mb={1} px={2}>
        <Typography className={textBold} variant="subtitle1">
          Valuation Details
        </Typography>
      </Box>

      <List disablePadding>
        {renderProperties()}

        <Divider variant="middle" component="li" />

        <CustomListItem
          label="Total Fee"
          value={formatCurrency(Number(valuationInfo?.fee) || 0)}
        />
      </List>
    </Paper>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Valuation Information
          </Typography>
        </Box>

        {renderValuationDetails()}

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            testId="valuation-continue"
            color="primary"
            variant="contained"
            onClick={props.handleNextStep}
          >
            Continue
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
});